/**
 * StatsigService - Direct HTTP API calls to Statsig (no SDK)
 * Uses fetch API for browser compatibility
 */

const STATSIG_BASE_URL = 'https://statsigapi.net/v1';

// Global state for user info
if (!window._statsigUser) {
  window._statsigUser = {};
}

// Global account getter function - set by Angular during init
if (!window._statsigAccountGetter) {
  window._statsigAccountGetter = null;
}

const StatsigService = {
  /**
   * Get API key from environment
   */
  _getApiKey() {
    return window.LEO_STATSIG_CLIENT_KEY || 'client-LK4nQ6KAvtlOpw898XuTpLbIHeVse9hHQFE1tdB4QTS';
  },

  /**
   * Get current user object
   */
  _getUser() {
    return window._statsigUser;
  },

  /**
   * Set account getter function - called by Angular to provide account context
   * @param {Function} getter - Function that returns current account_id
   */
  setAccountGetter(getter) {
    window._statsigAccountGetter = getter;
  },

  /**
   * Get current account_id using the registered getter
   * @returns {number|string|null} - Current account_id or null
   */
  _getCurrentAccountId() {
    if (typeof window._statsigAccountGetter === 'function') {
      try {
        return window._statsigAccountGetter();
      } catch (e) {
        console.warn('[StatsigService] Account getter failed:', e);
        return null;
      }
    }
    return null;
  },

  /**
   * Make API call to Statsig
   */
  _apiCall(endpoint, data) {
    const apiKey = this._getApiKey();
    
    return fetch(STATSIG_BASE_URL + endpoint, {
      method: 'POST',
      headers: {
        'statsig-api-key': apiKey,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
    .then(function(response) {
      if (!response.ok) {
        throw new Error('HTTP ' + response.status);
      }
      return response.json();
    });
  },

  /**
   * Set user for targeting
   * @param {Object} user - User object with userID and other properties
   */
  setUser(user) {
    window._statsigUser = user || {};
  },

  /**
   * Check a feature gate value
   * Automatically injects account_id if not already present in user.custom
   * @param {string} gateName - The name of the gate to check
   * @param {Object} context - Optional additional context to merge into user.custom
   * @returns {Promise<boolean>} - Promise resolving to gate value
   */
  checkGate(gateName, context) {
    if (!gateName) {
      console.warn('[StatsigService] Gate name is required');
      return Promise.resolve(false);
    }

    const baseUser = this._getUser();
    const existingCustom = baseUser.custom || {};
    
    // Auto-inject account_id if not already present
    let accountId = existingCustom.account_id;
    if (accountId == null) {
      accountId = this._getCurrentAccountId();
    }

    // Merge context with existing custom, auto-injecting account_id
    const mergedCustom = {
      ...existingCustom,
      account_id: accountId,
      ...(context || {})
    };

    // Build final user object
    const userData = {
      ...baseUser,
      custom: mergedCustom,
      statsigEnvironment: baseUser.statsigEnvironment || {
        tier: this.getEnvironmentTier()
      }
    };

    return this._apiCall('/check_gate', {
      user: userData,
      gateName: gateName,
    })
    .then(function(data) {
      const value = data.value || false;
      return value;
    })
    .catch(function(error) {
      console.warn('[StatsigService] Failed to check gate ' + gateName + ':', error);
      return false;
    });
  },

  /**
   * Update user
   * @param {Object} user - User object
   */
  updateUser(user) {
    this.setUser(user);
  },

  /**
   * Get environment tier
   * @returns {string}
   */
  getEnvironmentTier() {
    if (window.LEO_DEPLOY_ENVIRONMENT === 'beta') {
      return 'staging';
    }
    else if (window.LEO_DEPLOY_ENVIRONMENT === 'prod') {
      return 'production';
    }
    return 'development';
  },

  // Contextual gates
  _contextualGates: new Set(),

  activateContextualGate(gateName) {
    if (gateName) this._contextualGates.add(gateName);
  },

  deactivateContextualGate(gateName) {
    if (gateName) this._contextualGates.delete(gateName);
  },

  clearContextualGates() {
    this._contextualGates.clear();
  },

  getActiveGates(userGates = []) {
    return [...new Set([...userGates, ...this._contextualGates])];
  }
};

// --- AngularJS Support ---
if (typeof angular !== "undefined") {
  try {
    const app = angular.module("leonardo");
    app.factory("StatsigService", ['AccountUserService', function (AccountUserService) {
      // Register account getter to auto-inject account_id in checkGate calls
      StatsigService.setAccountGetter(function() {
        var currentAccountUser = AccountUserService.currentAccountUser;
        if (currentAccountUser && currentAccountUser.account) {
          return currentAccountUser.account.id || currentAccountUser.account;
        }
        return null;
      });
      
      return StatsigService;
    }]);
  } catch (e) {
    console.error("Failed to register StatsigService with AngularJS:", e);
  }
}

// --- CommonJS / React Support ---
if (typeof module !== "undefined" && module.exports) {
  module.exports = { StatsigService };
}
